/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { FaqModuleItem } from '@coursera-web/cds-labs';
import { FAQsModule } from '@coursera-web/cds-labs';
import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { CML, toPlainText } from 'bundles/cml';
import FinaidLink from 'bundles/enroll/components/finaid/FinaidLink';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import HelpCenterInfo from 'bundles/unified-common/components/HelpCenterInfo';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import convertToCmlContent from 'bundles/unified-description-page-common/utils/convertToCmlContent';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment | ConsumerSpecializationPageFieldsFragment;
  config?: {
    disableCta?: boolean;
  };
};

const styles = {
  finAid: css`
    .rc-FinaidLink {
      display: flex;
      border-top: 2px solid var(--cds-color-neutral-stroke-primary-weak);
      margin-top: var(--cds-spacing-300);
      padding-top: var(--cds-spacing-300);
    }

    .caption-text,
    button.finaid-link {
      ${typography2.bodyPrimary}
      font-size: var(--cds-font-size-175) !important;
      margin-top: 0;
    }

    .caption-text {
      color: var(--cds-color-neutral-primary-weak);
    }
  `,
};

const FAQsModuleRenderer = ({ pageData, config }: Props) => {
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'faq',
      },
      ...eventingProductData,
    }
  );

  const convertedFaqs = pageData.cmlFaqs?.map((faq): FaqModuleItem => {
    return {
      question: toPlainText(convertToCmlContent(faq.question as CmlContent)) || '',
      answer: <CML cml={faq.answer as CmlContent} />,
    };
  });

  let finAidProps;
  if (pageData.__typename === 'DescriptionPage_CoursePage') {
    finAidProps = {
      courseId: pageData.id,
      course: new CoursesV1({
        slug: pageData.slug,
        id: pageData.id,
        name: pageData.name,
        courseType: pageData.courseType,
        courseStatus: pageData.courseStatus,
        certificates: pageData.certificates,
        premiumExperienceVariant: pageData.premiumExperienceVariant,
      }),
    };
  } else if (pageData.__typename === 'DescriptionPage_SpecializationPage') {
    finAidProps = {
      s12nId: pageData.id,
      courseIds: pageData.courses?.map(({ id }: { id: string }) => id),
      s12n: new OnDemandSpecializationsV1({
        slug: pageData.slug,
        id: pageData.id,
        name: pageData.name,
        productVariant: pageData.productVariant,
      }),
    };
  }

  return (
    <Section ref={visibilityTrackingRef}>
      <FAQsModule
        faqs={convertedFaqs}
        accordionTrackingData={eventingProductData}
        moreInfo={
          !config?.disableCta && (
            <>
              <HelpCenterInfo />
              {finAidProps && (
                <Typography2 variant="bodyPrimary" component="span" css={styles.finAid}>
                  <FinaidLink
                    {...finAidProps}
                    canOpenFinAidOnMount={false}
                    applyTextComponent={(link: React.ReactNode) => {
                      return <FormattedMessage message={_t(`Financial aid available, {link}`)} link={link} />;
                    }}
                    applyText={_t('learn more')}
                    alreadyAppliedText=""
                    wrapperClass="reset"
                  />
                </Typography2>
              )}
            </>
          )
        }
      />
    </Section>
  );
};

export default FAQsModuleRenderer;
